/**
 * Anonymous preview copy of the public connection catalog.
 *
 * The signed-in experience always renders the live registry. This list lets the
 * no-login Chrome review show the same complete set without credentials or API
 * access. Keep it limited to public labels and descriptions; never add secrets.
 */

import type { CatalogProvider } from "./integration-catalog.js";

type Row = readonly [
  platform: string,
  category: string,
  label: string,
  description: string,
  connectKind?: CatalogProvider["connectKind"],
  flags?: Partial<Pick<CatalogProvider, "isEsp" | "isCrm">>,
];

const ROWS: Row[] = [
  ["ultracart", "store", "UltraCart", "Your store. Orders, products, customers.", "bespoke"],
  ["shopify", "store", "Shopify", "Your Shopify store. Orders, products, customers."],
  ["woocommerce", "store", "WooCommerce", "Your WordPress/WooCommerce store. Orders, products, customers."],
  ["bigcommerce", "store", "BigCommerce", "Your BigCommerce store. Orders, products, customers."],
  ["amazon", "store", "Amazon", "Amazon Seller Central orders. Amazon hides buyer identity, so this channel tracks revenue and margins, not customer LTV."],
  ["konnektive", "store", "Konnektive", "Konnektive / Checkout Champ. If it is your system of record, this brings in orders, rebills, refunds, and customers."],
  ["buygoods", "store", "BuyGoods", "New BuyGoods orders, rebills, refunds, and chargebacks. Past orders need an export."],
  ["kajabi", "store", "Kajabi", "Your Kajabi orders. Course, membership, and coaching sales."],
  ["clickfunnels", "store", "ClickFunnels", "Your ClickFunnels 2.0 orders. Funnel sales, one-time purchases, and subscriptions."],
  ["recharge", "subscription_state", "Recharge", "Subscription status, MRR, churn, next bills, and failed payments. Order rebills stay with your store."],
  ["skio", "subscription_state", "Skio", "Subscription status, MRR, churn, next bills, and failed payments. Order rebills stay with your store."],
  ["bold_subscriptions", "subscription_state", "Bold Subscriptions", "Subscription status, MRR, churn, and next bills. Order rebills stay with your store."],
  ["meta_ads", "ad_platform", "Meta Ads", "Facebook and Instagram ad spend, pulled daily by campaign. Powers channel CAC."],
  ["google_ads", "ad_platform", "Google Ads", "Google ad spend, pulled daily by campaign. Powers channel CAC."],
  ["microsoft_ads", "ad_platform", "Microsoft Ads", "Microsoft and Bing ad spend, pulled daily by campaign. Powers channel CAC."],
  ["linkedin_ads", "ad_platform", "LinkedIn Ads", "LinkedIn ad spend, pulled daily by campaign. Powers channel CAC."],
  ["taboola", "ad_platform", "Taboola", "Taboola native-ad spend, pulled daily by campaign. Powers channel CAC."],
  ["tiktok_ads", "ad_platform", "TikTok Ads", "TikTok ad spend, pulled daily by campaign. Powers channel CAC."],
  ["amazon_ads", "ad_platform", "Amazon Ads", "Sponsored Products and Brands spend, pulled daily by campaign. Powers channel CAC."],
  ["pinterest_ads", "ad_platform", "Pinterest Ads", "Pinterest ad spend, pulled daily by campaign. Powers channel CAC."],
  ["snapchat_ads", "ad_platform", "Snapchat Ads", "Snapchat ad spend, pulled daily by campaign. Powers channel CAC."],
  ["hasoffers", "attribution", "HasOffers", "Affiliate attribution and commissions. Powers your affiliate CAC.", "bespoke"],
  ["clickbank", "attribution", "ClickBank", "ClickBank affiliate sales and what they cost you. Powers affiliate CAC."],
  ["everflow", "attribution", "Everflow", "Everflow affiliate conversions and commissions. Powers affiliate CAC."],
  ["refersion", "attribution", "Refersion", "Refersion affiliate and influencer sales plus commissions. Powers affiliate CAC."],
  ["impact", "attribution", "Impact.com", "Impact.com affiliate and partnership conversions plus commissions. Powers affiliate CAC."],
  ["awin", "attribution", "Awin", "Awin affiliate conversions and commissions. Powers affiliate CAC."],
  ["digistore24", "attribution", "Digistore24", "Digistore24 sales and affiliate commissions. Powers affiliate CAC."],
  ["stripe_fees", "cost_actualization", "Stripe", "Replaces estimated processing fees with Stripe's real settled fees. Never pulls orders."],
  ["paypal_fees", "cost_actualization", "PayPal", "Replaces estimated processing fees with PayPal's real settled fees. Never pulls orders."],
  ["shipstation", "cost_actualization", "ShipStation", "Replaces estimated shipping with real postage. Never pulls orders."],
  ["klaviyo", "esp", "Klaviyo", "Your email and SMS platform. Push customer segments to it.", "esp", { isEsp: true }],
  ["mailchimp", "esp", "Mailchimp", "Your email platform. Push customer segments to it.", "esp", { isEsp: true }],
  ["ontraport", "esp", "Ontraport", "Your CRM and email platform. Push customer segments and approved profit fields to existing contacts.", "esp", { isEsp: true, isCrm: true }],
  ["attentive", "esp", "Attentive", "Your SMS and email platform. Push customer segments to it.", "esp", { isEsp: true }],
  ["postscript", "esp", "Postscript", "Your SMS marketing platform. Push customer segments to it.", "esp", { isEsp: true }],
  ["omnisend", "esp", "Omnisend", "Your email and SMS platform. Push customer segments to it.", "esp", { isEsp: true }],
  ["hubspot", "crm", "HubSpot", "Pull HubSpot commerce orders when it is your system of record, and push approved profit fields to existing contacts.", "fields", { isCrm: true }],
  ["activecampaign", "crm", "ActiveCampaign", "Push approved profit fields and customer segments to existing ActiveCampaign contacts.", "fields", { isCrm: true }],
  ["keap", "crm", "Keap", "Push approved profit fields and customer segments to existing Keap contacts.", "fields", { isCrm: true }],
  ["gohighlevel", "crm", "GoHighLevel", "Push approved profit fields and customer segments to existing GoHighLevel contacts.", "fields", { isCrm: true }],
  ["quickbooks", "accounting", "QuickBooks", "Pull monthly overhead from your books so the dashboard can show operating profit and cash context."],
  ["xero", "accounting", "Xero", "Pull monthly overhead from your books so the dashboard can show operating profit and cash context."],
  ["freshbooks", "accounting", "FreshBooks", "Pull monthly overhead from your books so the dashboard can show operating profit and cash context."],
  ["mixpanel", "analytics", "Mixpanel", "Daily event counts for funnel analysis. Your order source remains the revenue truth."],
  ["google_analytics", "analytics", "Google Analytics 4", "Sessions, key events, and traffic by landing page and channel. Your order source remains the revenue truth."],
];

export const ONBOARDING_PREVIEW_PROVIDERS: CatalogProvider[] = ROWS.map(([
  platform,
  category,
  label,
  description,
  connectKind = "fields",
  flags = {},
]) => ({
  platform,
  category,
  label,
  description,
  required: category === "store",
  comingSoon: false,
  connectKind,
  showInOnboarding: true,
  syncActions: [],
  ...flags,
}));
